// Shared catalog of OpenUse flagship products. Single source of truth used
// across the Home, Solutions, Product, and Industries pages so positioning
// and copy stay consistent.

export interface WorkflowStep {
  title: string;
  description: string;
  /** Optional per-step screenshot. Falls back to the product `heroImage` if omitted. */
  image?: string;
}

export interface FlagshipProduct {
  /** URL slug, used for routing and anchor links */
  slug: 'supplymd' | 'openrfq' | 'ainventory' | 'atracker';
  name: string;
  /** Short positioning line shown on cards and product hero */
  tagline: string;
  /** Buyer / target audience, one line */
  audience: string;
  /** Longer paragraph for product hero */
  description: string;
  /**
   * Path under /public/images used as the branded hero artwork for this
   * product. This slot is reserved for brand imagery (logo lockup / key art)
   * and should NOT be a raw app screenshot — screenshots belong on the
   * workflow steps below.
   */
  heroImage: string;
  /** Live App Store listing URL, if the product is published. */
  appStoreUrl?: string;
  /**
   * When true, the product page sends visitors straight into the self-serve
   * App Store signup flow (primary CTA + getting-started steps) instead of
   * the sales-lead "Request a demo" form.
   */
  selfServe?: boolean;
  /** Self-serve "how to get started" steps, shown only when `selfServe` is true. */
  gettingStarted?: { title: string; description: string }[];
  /** 4-step workflow for the product detail page */
  workflow: WorkflowStep[];
  /** Capabilities list */
  features: string[];
  /** Outcomes list */
  benefits: string[];
}

export const flagshipProducts: FlagshipProduct[] = [
  {
    slug: 'ainventory',
    name: 'AInventory',
    tagline: 'Fast, mobile-first inventory management with barcode scanning and AI.',
    audience:
      'SMBs across medical, dental, retail, restaurants, warehouses, schools, and field service.',
    description:
      'AInventory helps businesses receive, track, transfer, count, and manage inventory with powerful barcode scanning and an intuitive mobile-first workflow. Whether you manage medical supplies, retail products, restaurant inventory, office equipment, or warehouse stock, AInventory keeps your inventory accurate and organized — so you spend less time managing inventory and more time growing your business.',
    heroImage: '/images/products/ainventory/ainventory-cover.jpg',
    appStoreUrl: 'https://apps.apple.com/app/ainventory/id6791265816',
    selfServe: true,
    gettingStarted: [
      {
        title: 'Download AInventory',
        description: 'Get the app free from the App Store on your iPhone or iPad — no sales call required.',
      },
      {
        title: 'Create your free account',
        description: 'Sign up in seconds with Firebase-secured login and start your own workspace.',
      },
      {
        title: 'Scan your first item',
        description: 'Point your camera at a barcode and see AInventory in action right away.',
      },
    ],
    workflow: [
      {
        title: '1. Scan any barcode, instantly',
        description:
          'Scan UPC, EAN, Code128, and QR codes with fast, on-device recognition. Unknown barcodes launch a guided workflow, and you can generate internal barcodes for items that do not have one.',
        image: '/images/products/ainventory/ainventory-barcode-scan.png',
      },
      {
        title: '2. Run every inventory operation from your phone',
        description:
          'Receive inventory, transfer between locations, run cycle counts, adjust quantities, and look up items — all from a single mobile-first workflow built for the floor, not the desk.',
        image: '/images/products/ainventory/ainventory-operations.png',
      },
      {
        title: '3. Manage multiple warehouses and storage bins',
        description:
          'Model multiple warehouses and storage bins, track real-time quantities, and review a complete inventory history for every item and location.',
        image: '/images/products/ainventory/ainventory-inventory.png',
      },
      {
        title: '4. Identify items with AI assistance',
        description:
          'Apple Vision–powered suggestions help identify products and speed up item creation. Snap a photo and let AInventory pre-fill product details so your catalog stays clean and complete.',
        image: '/images/products/ainventory/ainventory-ai-identify.png',
      },
      {
        title: '5. Collaborate across your team in the cloud',
        description:
          'Firebase-powered cloud sync keeps everyone in step. Invite teammates, assign role-based permissions, and give each user the right level of access to inventory and operations.',
        image: '/images/products/ainventory/ainventory-teamwork.png',
      },
    ],
    features: [
      'Fast barcode scanning for UPC, EAN, Code128, and QR codes.',
      'Intelligent item lookup and unknown-barcode workflow.',
      'Generate internal barcodes for untagged items.',
      'Receive, transfer, cycle count, adjust, and look up inventory.',
      'Multiple warehouses, storage bins, and real-time quantities.',
      'Complete inventory history for every item and location.',
      'Multi-user collaboration with role-based permissions.',
      'Cloud synchronization powered by Firebase.',
      'AI-assisted item identification with Apple Vision.',
      'Quick product creation from photos and suggestions.',
    ],
    benefits: [
      'Keep inventory accurate across every location and team.',
      'Cut counting, receiving, and transfer time with mobile-first workflows.',
      'Give every user the right access with role-based permissions.',
      'Spend less time managing inventory and more time growing the business.',
    ],
  },
  {
    slug: 'supplymd',
    name: 'SupplyMD',
    tagline: 'Medical supply management built for iPhone and iPad.',
    audience:
      'Healthcare professionals, small clinics, pharmacies, and mobile providers.',
    description:
      'SupplyMD is an intuitive iOS app that helps healthcare teams take charge of their medical supplies. Designed with an Apple-inspired interface, it streamlines barcode scanning, batch tracking, expiration alerts, and dispense workflows — so clinicians can stay prepared, reduce waste, and focus on patient care.',
    heroImage: '/images/products/supplymd/supplymd-cover.jpg',
    appStoreUrl: 'https://apps.apple.com/us/app/supplymd/id6746823526',
    selfServe: true,
    gettingStarted: [
      {
        title: 'Download SupplyMD',
        description: 'Get the app free from the App Store on your iPhone or iPad — no sales call required.',
      },
      {
        title: 'Sign in with your Apple ID',
        description: 'CloudKit keeps your data synced and backed up automatically across devices.',
      },
      {
        title: 'Scan your first item',
        description: 'Point your camera at a barcode to add stock and start tracking supplies right away.',
      },
    ],
    workflow: [
      {
        title: '1. Organize supplies room by room',
        description:
          'Add, edit, and organize items with ease. Set custom reorder levels per item so the right supplies are always ready when your team needs them.',
        image: '/images/products/supplymd/supplymd-mapping.png',
      },
      {
        title: '2. Scan barcodes to receive and dispense',
        description:
          'Scan barcodes to add stock instantly. SupplyMD identifies existing items for quick updates or pre-fills new items with scanned data, and records every dispense with full traceability.',
        image: '/images/products/supplymd/supplymd-capture.png',
      },
      {
        title: '3. Track expirations with smart alerts',
        description:
          'Label stock with expiration dates and receive customizable notifications 7, 14, 30, or 60 days ahead — so expiring supplies get used before they go to waste.',
        image: '/images/products/supplymd/supplymd-replenish.png',
      },
      {
        title: '4. Manage batches, recalls, and reports',
        description:
          'Generate batch reports, track history, initiate recalls, and update expiration dates across related stock. Color-coded alerts highlight expiring batches for prioritized use.',
        image: '/images/products/supplymd/supplymd-report.png',
      },
    ],
    features: [
      'Effortless barcode scanning to add and update stock.',
      'Real-time inventory tracking with instant updates.',
      'Expiration labeling with 7, 14, 30, or 60-day alerts.',
      'Batch management with reports, recalls, and history.',
      'Custom reorder levels for timely restocking.',
      'Dispense tracking with full traceability.',
      'Team management with roles and shared inventory.',
      'CloudKit sync for secure backup across devices.',
      'Secure inventory reset protected by confirmations.',
      'In-app support ticket submission for fast help.',
    ],
    benefits: [
      'Reduce waste from expired medical supplies.',
      'Stay prepared with real-time stock visibility.',
      'Give clinicians a fast, familiar iOS workflow.',
      'Keep data safe and synced with Apple CloudKit.',
    ],
  },
  {
    slug: 'openrfq',
    name: 'OpenRFQ',
    tagline: 'Mobile-first RFQ sourcing for teams without enterprise procurement.',
    audience:
      'Small and medium businesses, manufacturers, procurement teams, and startups building supply chains.',
    description:
      'OpenRFQ helps businesses request quotes, compare suppliers, and negotiate pricing — all from a simple mobile app. Designed for organizations that do not have expensive procurement systems, OpenRFQ brings professional sourcing tools to every team so you can send RFQs, compare bids, negotiate, and award — right from your phone or tablet.',
    heroImage: '/images/products/openrfq/openrfq-cover.png',
    appStoreUrl: 'https://apps.apple.com/us/app/openrfq-rfq-sourcing/id1641606984',
    selfServe: true,
    gettingStarted: [
      {
        title: 'Download OpenRFQ',
        description: 'Get the app free from the App Store on your iPhone or iPad — no sales call required.',
      },
      {
        title: 'Create your free account',
        description: 'Sign up in seconds and set up your organization’s sourcing workspace.',
      },
      {
        title: 'Create your first RFQ',
        description: 'Publish a request for quotation and start comparing supplier quotes right away.',
      },
    ],
    workflow: [
      {
        title: '1. Create an RFQ in minutes',
        description:
          'Quickly create and publish Requests for Quotation with clear specifications and line items — no procurement software training required.',
        image: '/images/products/openrfq/openrfq-build.png',
      },
      {
        title: '2. Publish to suppliers and discover new ones',
        description:
          'Invite the suppliers you already work with and connect with new ones to expand your sourcing options and improve competition.',
        image: '/images/products/openrfq/openrfq-invite.png',
      },
      {
        title: '3. Compare quotes side by side',
        description:
          'View and compare multiple quotations in one place. Analyze pricing, quantities, and supplier responses to make confident decisions fast.',
        image: '/images/products/openrfq/openrfq-score.png',
      },
      {
        title: '4. Negotiate and award the supplier',
        description:
          'Communicate with suppliers directly through the RFQ, request revisions, negotiate better pricing, and award the business — all from your phone.',
        image: '/images/products/openrfq/openrfq-award.png',
      },
    ],
    features: [
      'Create and publish RFQs in minutes from a mobile app.',
      'Line items and specifications for clear supplier requests.',
      'Side-by-side comparison of supplier quotes.',
      'Built-in messaging for revisions and negotiation.',
      'Supplier discovery to expand sourcing options.',
      'Simple award workflow that closes the loop.',
    ],
    benefits: [
      'Get professional sourcing without enterprise procurement software.',
      'Reduce costs through structured supplier competition.',
      'Move faster from RFQ to award, all on mobile.',
      'Bring transparency to sourcing with a single source of truth.',
    ],
  },
  {
    slug: 'atracker',
    name: 'ATracker',
    tagline: 'Mobile-first asset tracking for teams on the move.',
    audience:
      'Facilities, field service, IT, warehouse, and clinical operations teams.',
    description:
      'ATracker is a modern mobile-first asset management platform that gives operational teams real-time visibility, accountability, and control over physical assets and inventory. Built for iPhone and iPad, it combines barcode and QR scanning, check-in/check-out workflows, and multi-location tracking into one streamlined experience — so tools, equipment, and leased assets stay accounted for from the warehouse to the field.',
    heroImage: '/images/products/atracker/atracker-cover.png',
    appStoreUrl: 'https://apps.apple.com/app/openasset-asset-tracker/id6771233256',
    selfServe: true,
    gettingStarted: [
      {
        title: 'Download ATracker',
        description: 'Get the app free from the App Store on your iPhone or iPad — no sales call required.',
      },
      {
        title: 'Create your free account',
        description: 'Sign up in seconds and start your own workspace.',
      },
      {
        title: 'Scan your first asset',
        description: 'Point your camera at a barcode or QR code to check in your first asset.',
      },
    ],
    workflow: [
      {
        title: '1. Organize every asset in one place',
        description:
          'Catalog tools, equipment, IT devices, and leased assets with categories, tags, and smart search so any team member can find what they need in seconds.',
        image: '/images/products/atracker/atracker-list.png',
      },
      {
        title: '2. Scan barcodes and QR codes on the spot',
        description:
          'Use the iPhone or iPad camera to check assets in and out, assign them to people or job sites, and update inventory without paperwork.',
        image: '/images/products/atracker/atracker-scan.png',
      },
      {
        title: '3. Track locations, assignments, and teams',
        description:
          'Move assets across offices, warehouses, clinics, and job sites with real-time visibility and role-based access for every operator involved.',
        image: '/images/products/atracker/atracker-location.png',
      },
      {
        title: '4. Maintain audit-ready history and reports',
        description:
          'Every movement, assignment, maintenance record, and lease renewal is captured automatically — ready for audits, renewals, and operational reviews.',
        image: '/images/products/atracker/atracker-report.png',
      },
    ],
    features: [
      'Barcode and QR code scanning from iPhone and iPad.',
      'Equipment check-in, check-out, and assignment tracking.',
      'Multi-location support across offices, warehouses, and job sites.',
      'Leased equipment, contracts, and renewal management.',
      'Complete asset history, maintenance logs, and audit trails.',
    ],
    benefits: [
      'Eliminate lost equipment and improve accountability.',
      'Cut manual tracking work with mobile-first capture.',
      'Stay ahead of lease renewals and maintenance schedules.',
      'Give every team a shared, up-to-date view of assets.',
    ],
  },
];

export const getProductBySlug = (slug: string): FlagshipProduct | undefined =>
  flagshipProducts.find((p) => p.slug === slug);
